import { useCallback, useSyncExternalStore } from "react";
import {
  subscribeClientInsights,
  getClientInsightsSnapshot,
  ensureClientInsights,
} from "./clientInsightsStore";
import {
  getThresholdDays,
  setThresholdDays,
  subscribeInsightPrefs,
} from "./insightPrefs";
import { deriveTags, EMPTY_TAGS } from "./insightDerive";

/**
 * Roster insight tags for the chat panels.
 *
 * `getTags` mirrors `useCommsIdentity`'s `getClientName` contract exactly: it is
 * a stable callback, safe to call during render, and a miss schedules the fetch
 * and re-renders when the data lands. That is what lets `ChatDockUserList` keep
 * building its rows in a non-memoized IIFE.
 *
 * @returns {{
 *   getTags: (clientId: string|number) => typeof EMPTY_TAGS,
 *   getInsight: (clientId: string|number) => Object|undefined,
 *   hasInsights: boolean,
 *   thresholdDays: number,
 *   setThresholdDays: (days: number) => void,
 *   insightsVersion: number,
 *   asOf: string|null
 * }}
 */
export function useClientInsights() {
  const snapshot = useSyncExternalStore(
    subscribeClientInsights,
    getClientInsightsSnapshot
  );
  const thresholdDays = useSyncExternalStore(
    subscribeInsightPrefs,
    getThresholdDays
  );

  const { byClient, version, asOf } = snapshot;

  const getTags = useCallback(
    (clientId) => {
      ensureClientInsights();
      if (clientId == null) return EMPTY_TAGS;
      const record = byClient.get(String(clientId));
      // A room whose client has no insight record derives to EMPTY_TAGS, which
      // matches no filter and renders no icon — so an unresolved row is never
      // mislabelled while data is in flight.
      return deriveTags(record, thresholdDays);
    },
    [byClient, thresholdDays]
  );

  // The raw record, for the dates the tag tooltips quote back.
  const getInsight = useCallback(
    (clientId) => (clientId == null ? undefined : byClient.get(String(clientId))),
    [byClient]
  );

  return {
    getTags,
    getInsight,
    // The single gate for showing the new chips and tags anywhere.
    hasInsights: byClient.size > 0,
    thresholdDays,
    setThresholdDays,
    // For panels whose row list is memoized (InboxList): without this in the
    // dependency array the list would never update when a batch lands, because
    // `byClient` is only swapped on a real change.
    insightsVersion: version,
    asOf,
  };
}
